import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Cloud, CloudOff, RefreshCw, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/AuthContext";
import { cloud } from "@/backend";
import { forceSyncRuns } from "@/lib/forceSync";
import { getNetworkStatus, addNetworkStatusListener } from "@/lib/platform";

import { safeLocalStorage } from '@/lib/browserStorage';
import { useFeaturePermissions } from '@/lib/FeaturePermissionContext';
export default function SyncStatusIndicator() {
  const { user } = useAuth();
  const { canUseFeature } = useFeaturePermissions();
  const canForceSync = canUseFeature("force_sync");
  const [open, setOpen] = useState(false);
  const [online, setOnline] = useState(true);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(() => safeLocalStorage.getItem("drilllog:lastSync") || null);
  const [error, setError] = useState("");
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const refreshPending = useCallback(async () => {
    if (!user?.id) return;
    try {
      const items = await cloud.getPendingSync(user.id);
      setPending(items.length);
    } catch {
      // Keep the last known value if local storage is temporarily unavailable.
    }
  }, [user?.id]);

  useEffect(() => {
    let disposed = false;
    let networkListener = null;
    const updateNetwork = async () => {
      const status = await getNetworkStatus();
      if (!disposed) setOnline(Boolean(status?.connected));
    };
    void updateNetwork();
    void addNetworkStatusListener((status) => { if (!disposed) setOnline(Boolean(status?.connected)); })
      .then((handle) => { networkListener = handle; })
      .catch(() => {});
    void refreshPending();
    const timer = window.setInterval(() => { void refreshPending(); void updateNetwork(); }, 15000);
    return () => {
      disposed = true;
      void networkListener?.remove?.();
      window.clearInterval(timer);
    };
  }, [refreshPending]);



  const handleSync = async () => {
    if (syncing || !user?.id) return;
    setSyncing(true);
    setError("");
    try {
      if (!online) throw new Error("Device is offline");
      if (cloud.isEnabled()) await cloud.syncNow(user.id);
      await forceSyncRuns();
      const now = new Date().toISOString();
      safeLocalStorage.setItem("drilllog:lastSync", now);
      setLastSync(now);
      await refreshPending();
    } catch (e) {
      setError(e?.message || "Sync failed");
      await refreshPending();
    } finally {
      setSyncing(false);
    }
  };

  const status = syncing ? "Syncing" : !online ? "Offline" : pending > 0 ? "Pending" : "Synced";
  const StatusIcon = syncing ? RefreshCw : !online ? CloudOff : pending > 0 ? AlertCircle : null;

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event) => {
      const target = event.target;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      const trigger = triggerRef.current;
      const popover = popoverRef.current;
      if (!trigger || !popover) return;
      const rect = trigger.getBoundingClientRect();
      const gap = 6;
      const margin = 10;
      const width = Math.min(280, window.innerWidth - margin * 2);
      const height = Math.min(popover.scrollHeight, window.innerHeight - margin * 2);
      let left = rect.right - width;
      left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));
      let top = rect.bottom + gap;
      if (top + height > window.innerHeight - margin) top = rect.top - height - gap;
      top = Math.max(margin, Math.min(top, window.innerHeight - height - margin));
      Object.assign(popover.style, { left: `${left}px`, top: `${top}px`, width: `${width}px`, maxHeight: `${height}px` });
    };
    reposition();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open, pending, syncing, error, lastSync]);

  const popover = open ? (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Sync Status"
      className="fixed z-[10000] overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900"
      style={{ visibility: "hidden" }}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-3 py-2.5">
        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">Sync Status</div>
        <div className="mt-1.5 space-y-0.5 text-xs leading-4 text-slate-500 dark:text-slate-300">
          <div>Last synced {lastSync ? new Date(lastSync).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "Not yet synced"}</div>
          <div>Pending changes: {pending}</div>
        </div>
        {error && <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">{error}</p>}
        <div className="mt-2 border-t border-slate-200 dark:border-slate-700 pt-1.5 flex items-center justify-between gap-2">
          <Button type="button" variant="ghost" onClick={handleSync} disabled={!canForceSync || syncing || !online} className="h-7 px-2 rounded-md gap-1.5 text-xs font-normal text-slate-900 dark:text-slate-100">
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Force Sync"}
          </Button>
          <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="h-7 px-2 rounded-md text-xs text-slate-500 dark:text-slate-300">Close</Button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <div className="relative">
        <Button ref={triggerRef} type="button" variant="outline" onClick={() => setOpen((value) => !value)} className={`rounded-xl h-11 gap-2 bg-white dark:bg-slate-900 dark:border-slate-800 dark:text-slate-100 ${open ? "bg-slate-100 dark:bg-slate-800" : ""}`} title="Sync status" aria-expanded={open} aria-haspopup="dialog">
          {StatusIcon ? <StatusIcon className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /> : <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" aria-hidden="true" />}
          <span>{status}</span>
        </Button>
      </div>
      {popover && createPortal(React.cloneElement(popover, { style: { ...popover.props.style, visibility: "visible" } }), document.body)}
    </>
  );
}
